import React, { useState } from 'react'
import Pagination from '../layout/Pagination'
import { formatMoney } from '../helpers/utils'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faMagnifyingGlass, faCalendar } from '@fortawesome/free-solid-svg-icons'
import ReactFilter from '../components/inputs/ReactFilterList'
import Toolbar from 'react-multi-date-picker/plugins/toolbar'
import DatePicker from 'react-multi-date-picker'
import { Table } from 'react-bootstrap'
import dayjs from '../utils/dayjsConfig'
import LoadingScreen from '../components/LoadingScreen'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme, alpha } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import { layoutMobileMediaQuery } from '../layout/layoutConstants'
import OrderStatusBlock from './OrderStatusBlock'
import { OrderMobileCard, OrderMobilePagination, OrderIndexMobileFilters } from './mobile'
import GoogleIcon from '../icon/GoogleIcon'
import MobileEmptyStateCard from '../components/MobileEmptyStateCard'

const OrderIndexView = ({
  data,
  filters,
  mobileFilterConfigs = [],
  fetchDataStatus,
  isFetching,
  title,
  updateAllSelectedOptions,
  filterVersion,
  handleDateFilter,
  selectedDates,
  searchInput,
  handleSearch,
  handleEnterSearch,
  clearSearch = () => {},
  resetReactFilters,
  isAnyFilterSelected,
  isMobileFiltersOpen = false,
  openMobileFilters = () => {},
  closeMobileFilters = () => {},
  appliedFilterGroupCount = 0,
  appliedFilterChips = [],
  draftFilterSelections = {},
  handleDraftSelectChange = () => {},
  draftDateRange = { start: null, end: null },
  handleDraftDateChange = () => {},
  clearDraftDateRange = () => {},
  clearAllMobileFilters = () => {},
  applyMobileFilters = () => {},
  pagination,
  handlePaginationClick,
  handlePagination
}) => {
  const theme = useTheme()
  const isMobileList = useMediaQuery(layoutMobileMediaQuery)
  const isNarrowForCalendar = useMediaQuery(theme.breakpoints.down('sm'))
  const [expandedOrderUuid, setExpandedOrderUuid] = useState(null)

  const toggleOrderCardExpand = (uuid) => {
    setExpandedOrderUuid((prev) => (prev === uuid ? null : uuid))
  }

  const showModel = (event, orderUuid) => {
    event.preventDefault()
    window.open(`/pedidos/${orderUuid}`, '_blank')
  }

  const openOrderInNewTab = (uuid) => {
    window.open(`/pedidos/${uuid}`, '_blank')
  }

  const getFormattedDate = (createdDate) => {
    const d = new Date(createdDate)
    return d.toLocaleString()
  }

  const filtersLoading = Boolean(fetchDataStatus?.filters)
  const datePickerMonths = isMobileList && isNarrowForCalendar ? 1 : 2

  const renderDatePickerControl = () => (
    <DatePicker
      disabled={filtersLoading}
      range
      rangeHover
      dateSeparator=" - "
      months={[
        'Enero',
        'Febrero',
        'Marzo',
        'Abril',
        'Mayo',
        'Junio',
        'Julio',
        'Agosto',
        'Septiembre',
        'Octubre',
        'Noviembre',
        'Diciembre'
      ]}
      weekDays={['Dom', 'Lun', 'Mar', 'Mie', 'Jue', 'Vie', 'Sab']}
      format="DD MMM"
      weekStartDayIndex={1}
      highlightToday={false}
      numberOfMonths={datePickerMonths}
      maxDate={dayjs().local().endOf('day').toDate()}
      onChange={(dateObjects) => handleDateFilter(dateObjects)}
      value={selectedDates}
      plugins={[
        <Toolbar
          key="datepicker-toolbar"
          position="bottom"
          names={{
            deselect: 'Deseleccionar'
          }}
          sort={['deselect']}
        />
      ]}
      render={(value, openCalendar) => {
        return (
          <div
            key="flr-date"
            className={`${isMobileList ? '' : 'mr-10'} ${filtersLoading ? 'react-filter-disabled' : ''}`}
          >
            <div className="react-filter-box filter-input-width-datepicker">
              <div
                title="Rango de fechas"
                onClick={!filtersLoading ? openCalendar : undefined}
                className="react-filter-box-clickable-zone"
              >
                <div className="react-filter-titles">
                  <div className="react-filter-title-text">
                    {value?.length > 0 ? (
                      Array.isArray(value) ? (
                        <div className="filter-title-overflow">{value.join(' - ')}</div>
                      ) : (
                        <div className="filter-title-overflow">{value}</div>
                      )
                    ) : (
                      <div className="filter-title-overflow">RANGO DE FECHAS</div>
                    )}
                  </div>
                  <FontAwesomeIcon
                    className="react-filter-title-chevron"
                    style={{
                      color:
                        value?.length > 0 ? '#ef823a' : filtersLoading ? '#D5D5D5' : '#597fa9'
                    }}
                    icon={faCalendar}
                  />
                </div>
              </div>
            </div>
          </div>
        )
      }}
    />
  )

  const renderMobileCards = () => {
    if (!data || data.length === 0) {
      return (
        <Box sx={{ mt: 1 }}>
          <MobileEmptyStateCard
            icon={<ArticleOutlinedIcon fontSize="small" />}
            title="No hay pedidos disponibles"
            description="Cuando existan pedidos para los filtros seleccionados, aparecerán aquí con su detalle."
          />
        </Box>
      )
    }

    return (
      <Box
        sx={(theme) => ({
          mt: 1,
          mb: 0.5,
          py: 2,
          px: { xs: 0.5, sm: 1 },
          mx: { xs: -0.5, sm: -1 },
          borderRadius: 2,
          bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.04)
        })}
      >
        <Grid container spacing={2.5}>
          {data.map((model, index) => (
            <OrderMobileCard
              key={model.uuid || index}
              model={model}
              getFormattedDate={getFormattedDate}
              openOrderInNewTab={openOrderInNewTab}
              expanded={expandedOrderUuid === model.uuid}
              onToggleExpand={() => toggleOrderCardExpand(model.uuid)}
            />
          ))}
        </Grid>
      </Box>
    )
  }

  const renderDesktopTable = () => (
    <Table hover responsive>
      <thead className="table-header-gray-mui">
        <tr>
          <th style={{ verticalAlign: 'middle', padding: '10px 25px', width: '10%' }}>
            FECHA Y HORA
          </th>
          <th style={{ verticalAlign: 'middle', width: '10%' }}># DE PEDIDO</th>
          <th style={{ verticalAlign: 'middle', width: '15%' }}>CÓDIGO DE CLIENTE</th>
          <th style={{ verticalAlign: 'middle', width: '20%' }}>NOMBRE DE CLIENTE</th>
          <th style={{ verticalAlign: 'middle', width: '15%' }}>CREADO POR</th>
          <th style={{ verticalAlign: 'middle', width: '10%' }}>TOTAL PEDIDO</th>
          <th style={{ verticalAlign: 'middle', width: '10%' }}>TOTAL FACTURA</th>
          <th style={{ verticalAlign: 'middle' }}>ESTADO</th>
        </tr>
      </thead>
      <tbody>
        {data && data.length > 0 ? (
          data.map((model, index) => (
            <tr className="cursor-pointer" key={model.uuid}>
              <td
                className="td-text-limited td-text-limited-space"
                onClick={(e) => showModel(e, model.uuid)}
              >
                {getFormattedDate(model['created_date'])}
              </td>
              <td className="td-text-limited" onClick={(e) => showModel(e, model.uuid)}>
                {model['order_db_ref']}
              </td>
              <td className="td-text-limited" onClick={(e) => showModel(e, model.uuid)}>
                {model['client_db_ref']}
              </td>
              <td className="td-text-limited" onClick={(e) => showModel(e, model.uuid)}>
                {model['client_name']}
              </td>
              <td className="td-text-limited" onClick={(e) => showModel(e, model.uuid)}>
                {model['salesman_db_ref']} - {model['salesman_name']}
              </td>
              <td className="td-text-limited" onClick={(e) => showModel(e, model.uuid)}>
                {formatMoney(Number(model['order_total']))}
              </td>
              <td className="td-text-limited" onClick={(e) => showModel(e, model.uuid)}>
                {model['invoice_total'] > 0 ? formatMoney(Number(model['invoice_total'])) : '-'}
              </td>
              <td onClick={(e) => showModel(e, model.uuid)}>
                <OrderStatusBlock model={model} />
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan="8" className="td-text-limited td-text-limited-space">
              {' '}
              No hay resultados
            </td>
          </tr>
        )}
      </tbody>
    </Table>
  )

  if (isFetching) {
    return <LoadingScreen text="Cargando..." />
  }

  const desktopFiltersBlock = (
    <>
      <h4 className="text-sub-title">Filtrar pedidos por:</h4>
      <div className="filters-search-container mb-10">

                  <GoogleIcon className="filter-icon-poc oc-icon-primary" name="page_info" size={22} />
        
        <div className="filters-container">
          {filters.map((filter, indx) => (
            <div key={`flr-${indx}`} className="mr-10">
              <ReactFilter
                className="mr-10"
                title={filter.key}
                identifier={filter.key}
                options={filter.value}
                resetWatcher={filterVersion}
                onSelectionChange={updateAllSelectedOptions}
                disabled={filtersLoading || !filter.value?.length}
              />
            </div>
          ))}
          {renderDatePickerControl()}
        </div>
        <div className="search-input-containter">
          <input
            value={searchInput}
            onChange={handleSearch}
            onKeyDown={handleEnterSearch}
            className="round-search-input"
            style={{ fontSize: 12 }}
            placeholder="Buscar por código o nombre"
          />
          {searchInput === '' && (
            <FontAwesomeIcon icon={faMagnifyingGlass} className="search-icon" />
          )}
          {searchInput && (
            <div
              title="Limpiar texto"
              onClick={resetReactFilters}
              className="round-search-input-x"
            >
              X
            </div>
          )}
        </div>
      </div>
      <div className="mb-20">
        {isAnyFilterSelected && (
          <a onClick={resetReactFilters} className="reset-filter cursor-pointer mb-20">
            Restablecer filtros aplicados
          </a>
        )}
      </div>
    </>
  )

  return (
    <>
      <div className="index-header detailed-container">
        <h2 className="mb-20">{title}</h2>
      </div>
      <div className="index-table">
        <div className="show-area">
          {isMobileList ? (
            <OrderIndexMobileFilters
              query={searchInput}
              handleSearchChange={handleSearch}
              handleSearchKeyDown={handleEnterSearch}
              clearSearch={clearSearch}
              openFilters={openMobileFilters}
              appliedFilterGroupCount={appliedFilterGroupCount}
              appliedFilterChips={appliedFilterChips}
              isOpen={isMobileFiltersOpen}
              closeFilters={closeMobileFilters}
              mobileFilterConfigs={mobileFilterConfigs}
              draftFilterSelections={draftFilterSelections}
              handleDraftSelectChange={handleDraftSelectChange}
              anyFilterEmpty={filtersLoading}
              draftDateRange={draftDateRange}
              handleDraftDateChange={handleDraftDateChange}
              clearDraftDateRange={clearDraftDateRange}
              clearAllFilters={clearAllMobileFilters}
              applyFilters={applyMobileFilters}
            />
          ) : (
            desktopFiltersBlock
          )}
          {isMobileList ? renderMobileCards() : renderDesktopTable()}
          {isMobileList && pagination.total_pages > 1 && handlePagination ? (
            <Box sx={{ mt: 1 }}>
              <OrderMobilePagination pagination={pagination} handlePagination={handlePagination} />
            </Box>
          ) : null}
        </div>
      </div>
      {!isMobileList && pagination.total_pages > 1 ? (
        <nav className="index-table">
          <div onClick={handlePaginationClick}>
            <Pagination
              current_page={pagination.current_page}
              total_pages={pagination.total_pages}
            />
          </div>
        </nav>
      ) : null}
    </>
  )
}
export default OrderIndexView
